import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import constants from '../const/Const';

const PermissionContext = createContext({});

export const PermissionProvider = ({ userData, children }) => {
  const {
    PERMISSION_CATEGORIES,
    PERMISSION_SUBCATEGORIES,
    PERMISSION_TYPES,
    PERMISSION_CODES,
  } = constants;

  const DEMO_ORGS = ['sams_qa'];

  const permissions = userData?.permissions;

  const isDemoOrg = DEMO_ORGS.includes(userData?.organisation?.name);

  const getPermissionType = (category, subcategory, child = null) => {
    let permissionSource = permissions?.[category]?.[subcategory];
    if (child) {
      permissionSource = permissionSource?.children?.[child];
    }
    return permissionSource?.permission_type ?? PERMISSION_TYPES.NONE;
  };

  const hasPermissionType = (
    permissionType,
    category,
    subcategory,
    child = null
  ) => {
    const userPermissionType = getPermissionType(category, subcategory, child);
    return userPermissionType === permissionType;
  };

  const getRuleEnginePermissions = (child, permissionType) => {
    return hasPermissionType(
      permissionType,
      PERMISSION_CATEGORIES.MANAGER_SETUP,
      PERMISSION_SUBCATEGORIES.RULE_ENGINE,
      child
    );
  };
  const sanctionPermissions = {
    EDIT: getRuleEnginePermissions(
      PERMISSION_CODES.SANCTION,
      PERMISSION_TYPES.EDIT
    ),
    NONE: getRuleEnginePermissions(
      PERMISSION_CODES.SANCTION,
      PERMISSION_TYPES.NONE
    ),
  };

  const amsPermissions = {
    EDIT: getRuleEnginePermissions(PERMISSION_CODES.AMS, PERMISSION_TYPES.EDIT),
    NONE: getRuleEnginePermissions(PERMISSION_CODES.AMS, PERMISSION_TYPES.NONE),
  };

  const pepPermissions = {
    EDIT: getRuleEnginePermissions(PERMISSION_CODES.PEP, PERMISSION_TYPES.EDIT),
    NONE: getRuleEnginePermissions(PERMISSION_CODES.PEP, PERMISSION_TYPES.NONE),
  };

  const riskRatingPermissions = {
    EDIT: getRuleEnginePermissions(
      PERMISSION_CODES.RISK_RATING,
      PERMISSION_TYPES.EDIT
    ),
    NONE: getRuleEnginePermissions(
      PERMISSION_CODES.RISK_RATING,
      PERMISSION_TYPES.NONE
    ),
  };

  const transactionScreeningPermissions = {
    EDIT: getRuleEnginePermissions(
      PERMISSION_CODES.TRANSACTION_SCREENING,
      PERMISSION_TYPES.EDIT
    ),
    NONE: getRuleEnginePermissions(
      PERMISSION_CODES.TRANSACTION_SCREENING,
      PERMISSION_TYPES.NONE
    ),
  };

  const legalPermissions = {
    EDIT: hasPermissionType(
      PERMISSION_TYPES.EDIT,
      PERMISSION_CATEGORIES.LEGAL,
      PERMISSION_CODES.LEGAL
    ),
    NONE: hasPermissionType(
      PERMISSION_TYPES.NONE,
      PERMISSION_CATEGORIES.LEGAL,
      PERMISSION_CODES.LEGAL
    ),
  };

  const corporatePermissions = {
    EDIT: hasPermissionType(
      PERMISSION_TYPES.EDIT,
      PERMISSION_CATEGORIES.CORPORATE,
      PERMISSION_CODES.CORPORATE
    ),
    NONE: hasPermissionType(
      PERMISSION_TYPES.NONE,
      PERMISSION_CATEGORIES.CORPORATE,
      PERMISSION_CODES.CORPORATE
    ),
  };

  const ipWhitelistingPermissions = {
    EDIT: hasPermissionType(
      PERMISSION_TYPES.EDIT,
      PERMISSION_CATEGORIES.MANAGER_SETUP,
      PERMISSION_CODES.SUPPRESSION_LOGIC,
      PERMISSION_CODES.IP_WHITELISTING
    ),
    NONE: hasPermissionType(
      PERMISSION_TYPES.NONE,
      PERMISSION_CATEGORIES.MANAGER_SETUP,
      PERMISSION_CODES.SUPPRESSION_LOGIC,
      PERMISSION_CODES.IP_WHITELISTING
    ),
  };

  const contextValue = useMemo(
    () => ({
      getPermissionType,
      hasPermissionType,
      getRuleEnginePermissions,
      sanctionPermissions,
      amsPermissions,
      pepPermissions,
      riskRatingPermissions,
      legalPermissions,
      corporatePermissions,
      transactionScreeningPermissions,
      ipWhitelistingPermissions,
      permissions,
      isDemoOrg,
    }),
    [permissions, isDemoOrg]
  );

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

PermissionProvider.propTypes = {
  userData: PropTypes.shape({
    permissions: PropTypes.shape({}),
    organisation: PropTypes.shape({
      name: PropTypes.string,
    }),
  }).isRequired,
  children: PropTypes.node.isRequired,
};

export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }
  return context;
};
